import React, { useState } from 'react';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { StatusIndicator } from '../ui/StatusIndicator';
import { CheckCircle2, Circle, ChevronDown, ChevronRight, Plus, Play, Trash2, Clock } from 'lucide-react';
import { Run } from '../../hooks/useRuns';
import { DraftSlotAsset } from '../../hooks/useDraft';
import { Template } from '../../hooks/useTemplates';
import { groupSlotsByScene } from '../../lib/sceneDefs';
import { RenderProps } from '../../lib/slotProps';

interface RunListProps {
  runs: Run[];
  loading: boolean;
  template: Template;
  onNewRun: () => void;
  onResumeDraft: (draftId: string, slotDraft: DraftSlotAsset[]) => void;
  onDeleteDraft: (draftId: string) => Promise<void>;
}

// ─── RunSceneRow ─────────────────────────────────────────────────────────────

interface RunSceneRowProps {
  label: string;
  slotIds: readonly string[];
  props: RenderProps;
  slotLabels: Record<string, string>;
  slotTypes: Record<string, string>;
}

const RunSceneRow: React.FC<RunSceneRowProps> = ({
  label,
  slotIds,
  props,
  slotLabels,
  slotTypes,
}) => {
  const [open, setOpen] = useState(false);

  const filledCount = slotIds.filter((id) => props[id] != null).length;
  const allFilled = filledCount === slotIds.length;

  return (
    <div className="border border-border-subtle rounded-md overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-3 py-2 bg-bg-secondary hover:bg-bg-tertiary transition-colors text-left"
      >
        <div className="flex items-center gap-1.5">
          {open ? (
            <ChevronDown className="w-3.5 h-3.5 text-text-tertiary flex-shrink-0" />
          ) : (
            <ChevronRight className="w-3.5 h-3.5 text-text-tertiary flex-shrink-0" />
          )}
          <span className="text-xs font-medium text-text-primary">{label}</span>
        </div>
        <Badge size="sm" variant={allFilled ? 'success' : 'info'}>
          {filledCount} / {slotIds.length}
        </Badge>
      </button>

      {open && (
        <div className="px-3 py-2 space-y-1 bg-bg-primary">
          {slotIds.map((slotId) => {
            const filled = props[slotId] != null;
            return (
              <div key={slotId} className="flex items-center gap-2 text-xs text-text-secondary">
                {filled ? (
                  <CheckCircle2 className="w-3.5 h-3.5 text-status-online flex-shrink-0" />
                ) : (
                  <Circle className="w-3.5 h-3.5 text-status-offline flex-shrink-0" />
                )}
                <span className="flex-1">{slotLabels[slotId] ?? slotId}</span>
                <Badge size="sm" variant="default">{slotTypes[slotId] ?? '—'}</Badge>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ─── RunRow ──────────────────────────────────────────────────────────────────

interface RunRowProps {
  run: Run;
  template: Template;
}

const RunRow: React.FC<RunRowProps> = ({ run, template }) => {
  const [open, setOpen] = useState(false);

  // Build lookup maps from the template's slot list.
  const slotLabels: Record<string, string> = {};
  const slotTypes: Record<string, string> = {};
  const templateSlotIds = new Set<string>();
  for (const s of template.slots) {
    slotLabels[s.slotId] = s.label;
    slotTypes[s.slotId] = s.type;
    templateSlotIds.add(s.slotId);
  }

  // Build scene buckets dynamically from slot metadata.
  const scenes = groupSlotsByScene(template.slots).map((g) => ({
    id: g.id,
    label: g.label,
    slotIds: g.slots.map((s) => s.slotId),
  }));

  const visibleSlots = template.slots.filter((s) => s.visible !== false);
  const totalSlots = visibleSlots.length;
  const filledSlots = visibleSlots.filter((s) => run.props[s.slotId] != null).length;

  const statusVariant = (): 'success' | 'info' | 'error' | 'warning' | 'default' => {
    if (run.status === 'completed') return 'success';
    if (run.status === 'failed') return 'error';
    if (run.status === 'queued') return 'warning';
    if (run.status === 'preprocessing' || run.status === 'rendering' || run.status === 'uploading') return 'info';
    return 'default';
  };

  return (
    <div className="border border-border-subtle rounded-md overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-3 bg-bg-secondary hover:bg-bg-tertiary transition-colors text-left"
      >
        <div className="flex items-center gap-3">
          {open ? (
            <ChevronDown className="w-4 h-4 text-text-secondary flex-shrink-0" />
          ) : (
            <ChevronRight className="w-4 h-4 text-text-secondary flex-shrink-0" />
          )}
          <div>
            <p className="text-sm font-medium text-text-primary">
              {run.createdAt.toLocaleDateString(undefined, {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
              })}{' '}
              {run.createdAt.toLocaleTimeString(undefined, {
                hour: '2-digit',
                minute: '2-digit',
              })}
            </p>
            <p className="text-xs text-text-tertiary mt-0.5">
              {filledSlots} / {totalSlots} slots filled
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge size="sm" variant={statusVariant()}>
            {run.status}
          </Badge>
          <StatusIndicator
            status={
              run.status === 'completed'
                ? 'completed'
                : run.status === 'failed'
                ? 'failed'
                : run.status === 'queued'
                ? 'queued'
                : 'rendering'
            }
            showDot
            size="sm"
            label=""
          />
        </div>
      </button>

      {open && (
        <div className="p-4 space-y-2 bg-bg-primary">
          {scenes.map((scene) => (
            <RunSceneRow
              key={scene.id}
              label={scene.label}
              slotIds={scene.slotIds}
              props={run.props}
              slotLabels={slotLabels}
              slotTypes={slotTypes}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// ─── DraftRow ─────────────────────────────────────────────────────────────────

interface DraftRowProps {
  run: Run;
  onResume: () => void;
  onDelete: () => void;
  deleting?: boolean;
}

function formatDate(date: Date): string {
  return (
    date.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }) +
    ' ' +
    date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
  );
}

const DraftRow: React.FC<DraftRowProps> = ({ run, onResume, onDelete, deleting = false }) => {
  const displayDate = run.updatedAt ?? run.createdAt;
  return (
    <div className="border border-border-subtle rounded-md overflow-hidden">
      <div className="flex items-start justify-between px-4 py-3 bg-bg-secondary gap-4">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-text-primary truncate">
            {run.name ?? 'Draft'}
          </p>
          <span className="flex items-center gap-1 text-xs text-text-tertiary mt-1">
            <Clock className="w-3 h-3" />
            {formatDate(displayDate)}
          </span>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <Badge size="sm" variant="warning">Draft</Badge>
          <Button
            variant="primary"
            size="sm"
            onClick={onResume}
            className="flex items-center gap-1.5"
          >
            <Play className="w-3.5 h-3.5" />
            Resume
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={onDelete}
            disabled={deleting}
            isLoading={deleting}
            className="flex items-center gap-1.5"
          >
            <Trash2 className="w-3.5 h-3.5" />
            Delete
          </Button>
        </div>
      </div>
    </div>
  );
};

// ─── RunList ─────────────────────────────────────────────────────────────────

export const RunList: React.FC<RunListProps> = ({
  runs,
  loading,
  template,
  onNewRun,
  onResumeDraft,
  onDeleteDraft,
}) => {
  const [deletingDraftId, setDeletingDraftId] = useState<string | null>(null);

  const handleDelete = async (draftId: string) => {
    setDeletingDraftId(draftId);
    try {
      await onDeleteDraft(draftId);
    } finally {
      setDeletingDraftId(null);
    }
  };

  const drafts = runs.filter((r) => r.status === 'draft');
  const nonDrafts = runs.filter((r) => r.status !== 'draft');

  return (
  <div className="space-y-4">
    {/* Action bar */}
    <div className="flex items-center justify-between">
      <h3 className="text-sm font-semibold text-text-primary">
        Runs for <span className="text-accent-red">{template.name}</span>
      </h3>
      <Button
        variant="primary"
        size="sm"
        onClick={onNewRun}
        className="flex items-center gap-1.5"
      >
        <Plus className="w-4 h-4" />
        New Run
      </Button>
    </div>

    {/* Run list */}
    {loading ? (
      <p className="text-sm text-text-tertiary text-center py-6">Loading runs…</p>
    ) : runs.length === 0 ? (
      <div className="text-center py-8 border border-dashed border-border-dashed rounded-md">
        <p className="text-sm text-text-secondary mb-3">No runs yet for this template and market.</p>
        <Button variant="primary" size="sm" onClick={onNewRun} className="flex items-center gap-1.5 mx-auto">
          <Plus className="w-4 h-4" />
          Start First Run
        </Button>
      </div>
    ) : (
      <div className="space-y-2">
        {drafts.map((run) => (
          <DraftRow
            key={run.id}
            run={run}
            onResume={() => onResumeDraft(run.id, run.slotDraft ?? [])}
            onDelete={() => handleDelete(run.id)}
            deleting={deletingDraftId === run.id}
          />
        ))}
        {nonDrafts.map((run) => (
          <RunRow key={run.id} run={run} template={template} />
        ))}
      </div>
    )}
  </div>
  );
};
